(() => {
  'use strict';

  if (window.MajProductGrid) {
    window.MajProductGrid.initAll(document);
    return;
  }

  const GRID_SELECTOR = '[data-maj-product-grid]';

  const standardCartSelectors = {
    component: '[data-maj-product-form]',
    button: '[data-maj-add-button]',
    label: '[data-maj-button-label]',
    cartCount: '[data-maj-cart-count]',
    defaultAddText: 'Add to cart',
  };

  const recommendationCartSelectors = {
    component: '[data-maj-rec-product-form]',
    button: '[data-maj-rec-add-button]',
    label: '[data-maj-rec-button-label]',
    cartCount: '[data-maj-rec-cart-count]',
    defaultAddText: 'Add to cart',
  };

  function readCartCount(element) {
    if (!element) return 0;
    const match = element.textContent?.match(/\d+/);
    return Number(match?.[0] || element.dataset.cartQuantity || 0);
  }

  function setupCartFeedback(root, selectors = standardCartSelectors) {
    root.querySelectorAll(selectors.component).forEach((component) => {
      if (component.dataset.majCartFeedbackReady === 'true') return;

      const form = component.querySelector('form');
      const button = component.querySelector(selectors.button);
      const label = component.querySelector(selectors.label);
      const cartCount = component.querySelector(selectors.cartCount);
      const errorMessage = component.querySelector('[ref="addToCartTextError"]');
      const quantitySelector = component.querySelector('quantity-selector-component');
      const quantityInput = quantitySelector?.querySelector('input[name="quantity"]');
      const defaultAddText = component.dataset.majAddText || selectors.defaultAddText;

      if (!form || !button || !label || !cartCount) return;
      component.dataset.majCartFeedbackReady = 'true';

      let previousCount = readCartCount(cartCount);
      let isPending = false;
      let stateTimer;

      const setIdleState = () => {
        const count = readCartCount(cartCount);
        label.textContent = count > 0 ? 'Add another' : defaultAddText;
        button.classList.remove('is-loading', 'is-added');
        button.removeAttribute('aria-busy');
      };

      const showAddedState = () => {
        isPending = false;
        window.clearTimeout(stateTimer);
        label.textContent = 'Added';
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.removeAttribute('aria-busy');

        if (quantityInput) {
          const minimum = quantityInput.min || '1';
          if (typeof quantitySelector?.setValue === 'function') quantitySelector.setValue(minimum);
          else quantityInput.value = minimum;
          quantitySelector?.updateButtonStates?.();
        }

        stateTimer = window.setTimeout(setIdleState, 1800);
      };

      const countObserver = new MutationObserver(() => {
        const currentCount = readCartCount(cartCount);
        cartCount.dataset.cartQuantity = String(currentCount);

        if (isPending && currentCount > previousCount) showAddedState();
        else if (!isPending) setIdleState();

        previousCount = currentCount;
      });

      countObserver.observe(cartCount, {
        attributes: true,
        attributeFilter: ['class'],
        characterData: true,
        childList: true,
        subtree: true,
      });

      if (errorMessage) {
        const errorObserver = new MutationObserver(() => {
          if (!isPending || errorMessage.classList.contains('hidden')) return;
          isPending = false;
          window.clearTimeout(stateTimer);
          setIdleState();
        });

        errorObserver.observe(errorMessage, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
        });
      }

      form.addEventListener('submit', () => {
        previousCount = readCartCount(cartCount);
        isPending = true;
        window.clearTimeout(stateTimer);
        label.textContent = 'Adding…';
        button.classList.add('is-loading');
        button.classList.remove('is-added');
        button.setAttribute('aria-busy', 'true');

        stateTimer = window.setTimeout(() => {
          if (!isPending) return;
          isPending = false;
          setIdleState();
        }, 10000);
      });
    });
  }

  function getSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setSessionValue(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Grid controls remain functional when browser storage is unavailable.
    }
  }

  function setupToolbar(root) {
    const toolbarForm = root.querySelector('[data-maj-toolbar-form]');
    const query = new URLSearchParams(window.location.search);

    root.querySelectorAll('[data-maj-query-sync]').forEach((control) => {
      if (control.type === 'checkbox') {
        control.checked = query.getAll(control.name).includes(control.value);
      } else if (query.has(control.name)) {
        control.value = query.get(control.name) || '';
      }
    });

    root.querySelectorAll('[data-maj-fallback-group]').forEach((group) => {
      const label = group.querySelector('[data-maj-filter-label]');
      const activeControls = [...group.querySelectorAll('[data-maj-query-sync]')].filter((control) =>
        control.type === 'checkbox' ? control.checked : control.value.trim() !== ''
      );

      if (label && activeControls.length > 0) {
        const count = group.dataset.majFallbackGroup === 'price' ? 1 : activeControls.length;
        label.textContent = `${label.dataset.label} (${count})`;
      }
    });

    const clearFilters = root.querySelector('[data-maj-clear-filters]');
    const hasUrlFilters = [...query.keys()].some((name) => name.startsWith('filter.'));
    if (clearFilters && hasUrlFilters) clearFilters.hidden = false;

    const submitToolbar = () => {
      if (!toolbarForm) return;
      if (typeof toolbarForm.requestSubmit === 'function') toolbarForm.requestSubmit();
      else toolbarForm.submit();
    };

    root.querySelectorAll('input[type="checkbox"][data-maj-auto-submit]').forEach((control) => {
      control.addEventListener('change', submitToolbar);
    });

    root.querySelectorAll('input[type="text"][data-maj-auto-submit]').forEach((control) => {
      control.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        submitToolbar();
      });

      control.addEventListener('change', () => {
        window.setTimeout(() => {
          if (!control.closest('.maj-filter__price')?.contains(document.activeElement)) submitToolbar();
        }, 0);
      });
    });

    root.querySelectorAll('[data-maj-sort-option]').forEach((option) => {
      option.addEventListener('change', () => {
        if (!option.checked || !toolbarForm) return;
        root.querySelector('[data-maj-sort-menu]')?.removeAttribute('open');
        submitToolbar();
      });
    });

    const popovers = root.querySelectorAll('[data-maj-popover]');
    popovers.forEach((popover) => {
      popover.addEventListener('toggle', () => {
        if (!popover.open) return;
        popovers.forEach((other) => {
          if (other !== popover) other.removeAttribute('open');
        });
      });
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') popovers.forEach((popover) => popover.removeAttribute('open'));
    });

    const grid = root.querySelector('.maj-grid__products');
    const gridButtons = root.querySelectorAll('[data-maj-columns]');
    const gridKey = root.dataset.majGridStorageKey || `maj_grid_columns_${root.id}`;
    const defaultColumns = root.dataset.majDefaultColumns || '4';
    const savedColumns = getSessionValue(gridKey) || defaultColumns;

    if (grid && window.matchMedia('(min-width: 990px)').matches) {
      grid.style.setProperty('--maj-grid-columns', savedColumns);
    }

    gridButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.majColumns === savedColumns ? 'true' : 'false');
      button.addEventListener('click', () => {
        const columns = button.dataset.majColumns;
        if (!columns) return;
        if (grid) grid.style.setProperty('--maj-grid-columns', columns);
        setSessionValue(gridKey, columns);
        gridButtons.forEach((item) => item.setAttribute('aria-pressed', item === button ? 'true' : 'false'));
      });
    });
  }

  function initGrid(root) {
    if (!(root instanceof Element) || root.dataset.majReady === 'true') return;
    root.dataset.majReady = 'true';
    window.MajWishlist?.refresh(root);
    setupCartFeedback(root, standardCartSelectors);
    setupToolbar(root);
  }

  function initAll(scope = document) {
    if (scope instanceof Element && scope.matches(GRID_SELECTOR)) initGrid(scope);
    scope.querySelectorAll?.(GRID_SELECTOR).forEach(initGrid);
  }

  class MajRecommendedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.recommendationsPerformed === 'true') {
        this.setAttribute('aria-busy', 'false');
        window.MajWishlist?.refresh(this);
        setupCartFeedback(this, recommendationCartSelectors);
        return;
      }

      if (!this.dataset.productId || !this.dataset.url) {
        this.hidden = true;
        return;
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0]?.isIntersecting) return;
            this.observer.disconnect();
            this.loadRecommendations();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.loadRecommendations();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }

    async loadRecommendations() {
      if (this.dataset.loading === 'true') return;
      this.dataset.loading = 'true';
      this.controller = new AbortController();

      try {
        const response = await fetch(this.dataset.url, {
          credentials: 'same-origin',
          signal: this.controller.signal,
        });

        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsedDocument.getElementById(this.id);

        if (!replacement) throw new Error('Recommendation section was not returned.');
        this.replaceWith(replacement);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[MAJ recommendations]', error);
        this.hidden = true;
      }
    }
  }

  const api = { initAll, initGrid, setupCartFeedback };
  window.MajProductGrid = api;

  if (!customElements.get('maj-recommended-products')) {
    customElements.define('maj-recommended-products', MajRecommendedProducts);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
})();
                    